import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "../../../../../app/rootReducer";
import {
  UploadReportRequest,
  UploadProgress,
  ApiListResponse,
} from "../../../../../types/partnerReports";
import {
  PartnerReport,
  ReportFilters,
} from "../../../../../api/partnerReports/types";
import {
  getReports,
  getPartnerReports,
  getReport,
  uploadReport,
  createReport,
  updateReport,
  deleteReport,
  processReport,
  retryReport,
  bulkDeleteReports,
} from "../../../../../api/partnerReports";

export interface ReportsState {
  reports: PartnerReport[];
  selectedReport: PartnerReport | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  uploadProgress: {
    isUploading: boolean;
    progress: number;
  };
}

const initialState: ReportsState = {
  reports: [],
  selectedReport: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  loading: false,
  error: null,
  uploadProgress: {
    isUploading: false,
    progress: 0,
  },
};

// Async Thunk Actions
export const fetchReportsAction = createAsyncThunk(
  "reports/fetchReports",
  async (
    filters: ReportFilters | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await getReports(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch reports"
      );
    }
  }
);

export const fetchPartnerReportsAction = createAsyncThunk(
  "reports/fetchPartnerReports",
  async (
    {
      partnerId,
      filters,
    }: {
      partnerId: string;
      filters?: {
        reportType?: string;
        status?: string;
        page?: number;
        limit?: number;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await getPartnerReports(partnerId, filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch partner reports"
      );
    }
  }
);

export const fetchReportAction = createAsyncThunk(
  "reports/fetchReport",
  async (id: string, { rejectWithValue }) => {
    try {
      const report = await getReport(id);
      return report;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch report"
      );
    }
  }
);

export const uploadReportAction = createAsyncThunk(
  "reports/uploadReport",
  async (
    request: UploadReportRequest,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const report = await uploadReport(request, (progress: UploadProgress) => {
        dispatch(
          setUploadProgress({
            isUploading: true,
            progress: progress.percentage,
          })
        );
      });

      // Reset upload progress
      dispatch(
        setUploadProgress({
          isUploading: false,
          progress: 0,
        })
      );

      return report;
    } catch (error: any) {
      dispatch(
        setUploadProgress({
          isUploading: false,
          progress: 0,
        })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to upload report"
      );
    }
  }
);

export const createReportAction = createAsyncThunk(
  "reports/createReport",
  async (
    data: {
      partnerId: string;
      title: string;
      description?: string;
      reportType: string;
      metadata?: any;
    },
    { rejectWithValue }
  ) => {
    try {
      const report = await createReport(data);
      return report;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create report"
      );
    }
  }
);

export const updateReportAction = createAsyncThunk(
  "reports/updateReport",
  async (
    { id, data }: { id: string; data: Partial<PartnerReport> },
    { rejectWithValue }
  ) => {
    try {
      const report = await updateReport(id, data);
      return report;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update report"
      );
    }
  }
);

export const deleteReportAction = createAsyncThunk(
  "reports/deleteReport",
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteReport(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete report"
      );
    }
  }
);

export const processReportAction = createAsyncThunk(
  "reports/processReport",
  async (id: string, { rejectWithValue }) => {
    try {
      const report = await processReport(id);
      return report;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to process report"
      );
    }
  }
);

export const retryReportAction = createAsyncThunk(
  "reports/retryReport",
  async (id: string, { rejectWithValue }) => {
    try {
      const report = await retryReport(id);
      return report;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to retry report"
      );
    }
  }
);

export const bulkDeleteReportsAction = createAsyncThunk(
  "reports/bulkDeleteReports",
  async (reportIds: string[], { rejectWithValue }) => {
    try {
      const result = await bulkDeleteReports(reportIds);
      return { reportIds, ...result };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to bulk delete reports"
      );
    }
  }
);

// Slice
const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    clearReportsError: (state) => {
      state.error = null;
    },
    setSelectedReport: (state, action: PayloadAction<PartnerReport | null>) => {
      state.selectedReport = action.payload;
    },
    setUploadProgress: (
      state,
      action: PayloadAction<{ isUploading: boolean; progress: number }>
    ) => {
      state.uploadProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch reports
      .addCase(fetchReportsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchReportsAction.fulfilled,
        (state, action: PayloadAction<ApiListResponse<PartnerReport>>) => {
          state.loading = false;
          state.reports = action.payload.data;
          state.totalCount = action.payload.total || 0;
          state.currentPage = action.payload.page || 1;
          state.pageSize = action.payload.limit || 20;
          state.error = null;
        }
      )
      .addCase(fetchReportsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch partner reports
      .addCase(fetchPartnerReportsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPartnerReportsAction.fulfilled,
        (state, action: PayloadAction<ApiListResponse<PartnerReport>>) => {
          state.loading = false;
          state.reports = action.payload.data;
          state.totalCount = action.payload.total || 0;
          state.currentPage = action.payload.page || 1;
          state.pageSize = action.payload.limit || 20;
          state.error = null;
        }
      )
      .addCase(fetchPartnerReportsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch single report
      .addCase(fetchReportAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchReportAction.fulfilled,
        (state, action: PayloadAction<PartnerReport>) => {
          state.loading = false;
          state.selectedReport = action.payload;
          state.error = null;
        }
      )
      .addCase(fetchReportAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Upload report
      .addCase(uploadReportAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        uploadReportAction.fulfilled,
        (state, action: PayloadAction<PartnerReport>) => {
          state.loading = false;
          state.reports.unshift(action.payload);
          state.totalCount += 1;
          state.error = null;
        }
      )
      .addCase(uploadReportAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create report
      .addCase(createReportAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createReportAction.fulfilled,
        (state, action: PayloadAction<PartnerReport>) => {
          state.loading = false;
          state.reports.unshift(action.payload);
          state.totalCount += 1;
          state.error = null;
        }
      )
      .addCase(createReportAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update report
      .addCase(updateReportAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateReportAction.fulfilled,
        (state, action: PayloadAction<PartnerReport>) => {
          state.loading = false;
          const index = state.reports.findIndex(
            (r) => r.id === action.payload.id
          );
          if (index !== -1) {
            state.reports[index] = action.payload;
          }
          if (state.selectedReport?.id === action.payload.id) {
            state.selectedReport = action.payload;
          }
          state.error = null;
        }
      )
      .addCase(updateReportAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete report
      .addCase(deleteReportAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteReportAction.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          state.reports = state.reports.filter((r) => r.id !== action.payload);
          state.totalCount = Math.max(0, state.totalCount - 1);
          if (state.selectedReport?.id === action.payload) {
            state.selectedReport = null;
          }
          state.error = null;
        }
      )
      .addCase(deleteReportAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Process report
      .addCase(processReportAction.pending, (state) => {
        state.error = null;
      })
      .addCase(
        processReportAction.fulfilled,
        (state, action: PayloadAction<PartnerReport>) => {
          const index = state.reports.findIndex(
            (r) => r.id === action.payload.id
          );
          if (index !== -1) {
            state.reports[index] = action.payload;
          }
          if (state.selectedReport?.id === action.payload.id) {
            state.selectedReport = action.payload;
          }
          state.error = null;
        }
      )
      .addCase(processReportAction.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // Retry report
      .addCase(retryReportAction.pending, (state) => {
        state.error = null;
      })
      .addCase(
        retryReportAction.fulfilled,
        (state, action: PayloadAction<PartnerReport>) => {
          const index = state.reports.findIndex(
            (r) => r.id === action.payload.id
          );
          if (index !== -1) {
            state.reports[index] = action.payload;
          }
          if (state.selectedReport?.id === action.payload.id) {
            state.selectedReport = action.payload;
          }
          state.error = null;
        }
      )
      .addCase(retryReportAction.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // Bulk delete reports
      .addCase(bulkDeleteReportsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        bulkDeleteReportsAction.fulfilled,
        (state, action: PayloadAction<{ reportIds: string[]; deleted: number }>) => {
          state.loading = false;
          state.reports = state.reports.filter(
            (r) => !action.payload.reportIds.includes(r.id)
          );
          state.totalCount = Math.max(0, state.totalCount - action.payload.deleted);
          state.error = null;
        }
      )
      .addCase(bulkDeleteReportsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Actions
export const { clearReportsError, setSelectedReport, setUploadProgress } =
  reportsSlice.actions;

// Selectors
export const selectReports = (state: RootState) => state.reports.reports;
export const selectSelectedReport = (state: RootState) =>
  state.reports.selectedReport;
export const selectReportsTotalCount = (state: RootState) =>
  state.reports.totalCount;
export const selectReportsCurrentPage = (state: RootState) =>
  state.reports.currentPage;
export const selectReportsPageSize = (state: RootState) =>
  state.reports.pageSize;
export const selectReportsLoading = (state: RootState) => state.reports.loading;
export const selectReportsError = (state: RootState) => state.reports.error;
export const selectReportsUploadProgress = (state: RootState) =>
  state.reports.uploadProgress;

export default reportsSlice.reducer;
